import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { AdminProfileKey, ProfileAlert } from './admin-profile.model';

export type ProfileAlertProvider = (profile: AdminProfileKey) => Observable<ProfileAlert[]>;

/**
 * Aggregates profile-scoped alerts shown on the Home page.
 *
 * Live providers can be registered at app startup (e.g. an "operator" provider
 * that polls failing recharge hooks). Until any provider is registered the
 * service returns an empty list and the Home page renders an "all clear" state.
 */
@Injectable({ providedIn: 'root' })
export class ProfileAlertsService {
  private providers: ProfileAlertProvider[] = [];

  registerProvider(provider: ProfileAlertProvider): void {
    this.providers.push(provider);
  }

  getAlerts(profile: AdminProfileKey): Observable<ProfileAlert[]> {
    if (this.providers.length === 0) return of([]);
    // Phase-1: serial pull-and-flatten. When real providers land we can switch
    // to combineLatest with caching.
    return new Observable<ProfileAlert[]>((subscriber) => {
      const collected: ProfileAlert[] = [];
      let pending = this.providers.length;
      const finish = () => {
        if (--pending === 0) {
          subscriber.next(collected);
          subscriber.complete();
        }
      };
      this.providers.forEach((p) => {
        p(profile).subscribe({
          next: (alerts) => collected.push(...alerts.filter((a) => a.profile === 'all' || a.profile === profile)),
          error: () => finish(),
          complete: () => finish(),
        });
      });
    });
  }
}
